/**
 * Learner-facing recovery copy for every lifecycle, capture and review code the
 * API can attach to an operation. Each message names what happened and the next
 * step; unknown codes fall back to a generic retry line. Keep this list in step
 * with the codes thrown by the workspace worker, the Coder adapter and the bridge.
 */
const recovery: Record<string, string> = {
  // Editor save barrier
  editor_untitled:
    'Name and save every untitled file in the current activity folder, then try again.',
  editor_outside_activity:
    'Save or close edited files outside the current activity folder, then try again.',
  editor_save_failed:
    'The editor could not save a file. Check its save error, save the file, then try again.',
  editor_changed: 'Files changed during capture. Pause editing and try again.',
  editor_context_changed:
    'The editor opened another activity. Reconnect to your current IDE before retrying.',
  editor_too_many_files:
    'Too many files are open for a safe save. Close unnecessary editors and try again.',
  editor_unsupported_document:
    'Save or close edited notebooks and unsupported editors, then try again.',
  editor_capture_interrupted: 'Saving was interrupted. Reconnect the IDE and try again.',
  editor_disconnected:
    'The editor disconnected before saving finished. Reconnect the IDE and try again.',
  editor_unavailable: 'The editor is not open in this workspace. Open the IDE, then try again.',
  editor_in_use:
    'This workspace belongs to another editor session. Save and close that editor, then reopen here.',
  editor_session_changed: 'Editor ownership changed. Reconnect the IDE and try again.',
  // Capture limits reported by the workspace bridge
  project_too_large:
    'Your activity folder exceeds the 1 MB save limit. Remove generated files and large data, then try again.',
  project_file_too_large:
    'A file in the activity folder is too large to save. Move large data out of the folder, then try again.',
  project_too_many_files:
    'The activity folder holds too many files. Remove generated folders such as virtual environments, then try again.',
  project_too_many_entries:
    'The activity folder holds too many entries. Remove generated folders such as virtual environments, then try again.',
  project_too_many_exclusions:
    'Too many files had to be excluded from the save. Remove generated folders, then try again.',
  project_too_deep: 'Folders are nested too deeply in the activity. Flatten them, then try again.',
  project_path_too_long: 'A file path in the activity is too long. Shorten it, then try again.',
  unsafe_project_path:
    'A file or link in the activity points outside the project. Remove it, then try again.',
  unsafe_project_root:
    'A file or link in the activity points outside the project. Remove it, then try again.',
  unsafe_path:
    'A file or link in the activity points outside the project. Remove it, then try again.',
  unsupported_source_encoding:
    'A source file is not UTF-8 text. Convert or remove it, then try again.',
  workspace_files_changed: 'Files changed while saving. Pause editing and try again.',
  // Workspace lifecycle
  workspace_context_changed: 'Your current activity changed. Reopen it before trying again.',
  workspace_stop_uncertain:
    'We are confirming whether your workspace paused. Your last confirmed backup is retained, and this resolves automatically within a few minutes. You can keep reading or sign out.',
  workspace_stop_failed:
    'Your workspace could not be paused. Your last saved checkpoint is retained; try again in a moment.',
  workspace_start_failed:
    'Your workspace could not start. Use Refresh IDE to try again; your last saved checkpoint is retained.',
  workspace_start_timeout:
    'Your workspace is taking longer than usual to start. Use Refresh IDE to try again in a minute.',
  workspace_transition_timeout:
    'Your workspace did not finish its previous change in time. Use Refresh IDE to try again.',
  workspace_unavailable:
    'Your workspace is not reachable right now. Use Refresh IDE to reconnect; your last saved checkpoint is retained.',
  workspace_unhealthy:
    'Your workspace is running but its editor agent is not responding. It will be paused automatically; use Refresh IDE to restart it.',
  workspace_recovery_required:
    'Your workspace needs recovery before it can continue. Use Refresh IDE to restore your last checkpoint.',
  workspace_owner_suspended: 'Your workspace account is suspended. Contact your programme team.',
  workspace_active:
    'Your workspace was in use, so the background backup was skipped. No action is needed.',
  workspace_busy:
    'Another operation is still running on your workspace. Wait for it to finish, then try again.',
  workspace_not_ready: 'Your workspace is not running yet. Wait until it is ready, then try again.',
  workspace_other_domain_active:
    'Workbench is using your coding environment. Save Workbench first, then open this workshop.',
  workspace_already_ready: 'Your workspace is already running. Reload to open it.',
  workspace_operation_failed:
    'The workspace could not complete this operation. Try again; your last saved checkpoint is retained.',
  workspace_output_too_large:
    'The workspace returned more data than can be saved. Remove generated files, then try again.',
  workspace_not_configured: 'Workshop compute is not configured in this environment.',
  workspace_identity_mismatch: 'Your workspace identity changed. Reopen the Workshop from Studio.',
  workspace_host_invalid:
    'The IDE address is not valid for your workspace. Reopen the Workshop from Studio.',
  workspace_bridge_unavailable:
    'The workspace file service did not respond. Use Refresh IDE and try again.',
  capacity_full: 'All Workshop workspaces are in use right now. Try again in a few minutes.',
  // Background jobs
  retry_exhausted:
    'This operation failed repeatedly. Start it again from the platform; your last saved checkpoint is retained.',
  lease_expired: 'The operation took too long and was released. Try again.',
  job_interrupted: 'The operation was interrupted by a service restart. Try again.',
  job_lease_lost: 'The operation was interrupted by a service restart. Try again.',
  job_failed: 'The operation could not finish. Try again; your last saved checkpoint is retained.',
  operation_expired: 'This operation is too old to retry. Start a new save from the platform.',
  operation_conflict: 'Another operation changed your workspace first. Reload and try again.',
  // Checkpoints and captured files
  checkpoint_invalid:
    'Your saved checkpoint could not be read. Use Refresh IDE to restore the last valid one.',
  checkpoint_required: 'A verified save is required first. Save your work, then try again.',
  checkpoint_conflict: 'Your checkpoint changed while this operation ran. Reload and try again.',
  checkpoint_changed: 'Your checkpoint changed while this operation ran. Reload and try again.',
  capture_storage_full:
    'The workspace disk is full, so files could not be saved. Delete generated files, then try again.',
  capture_discarded: 'The saved files were discarded before they were stored. Try again.',
  capture_digest_mismatch:
    'The saved files did not match what the editor reported. Pause editing and try again.',
  artifact_digest_mismatch:
    'The workshop files could not be verified in this environment, so nothing was changed. Try again; if it repeats, the workspace runtime needs an update.',
  capture_cleanup_failed:
    'Your files were saved, but temporary data could not be cleaned up. Try again.',
  capture_already_persisted: 'This save was already stored. Reload to see it.',
  // Workshop tasks and submissions
  task_not_executable: 'This workshop has no coding environment.',
  task_spec_invalid: 'This workshop’s runtime definition is invalid. Contact your programme team.',
  attempt_closed: 'This attempt is closed. Reload the workshop.',
  submission_empty: 'Save at least one source file in the activity before submitting.',
  submission_too_large:
    'This submission exceeds the review size limit. Remove generated files and unnecessary data, then submit again.',
  submission_limit_reached:
    'You have used all of your submissions for this workshop. The reference solution is shown with your last review.',
  submission_not_ready:
    'This attempt is not ready for another submission. Reload the review to see its current state.',
  submission_evidence_invalid:
    'The saved submission could not be verified. Save your files and submit again.',
  submission_artifact_invalid:
    'The saved submission could not be read. Save your files and submit again.',
  evaluation_input_too_large:
    'Your submission is too large to review. Remove generated files, then submit again.',
  evaluation_version_unavailable:
    'The review service changed while your review was queued. Submit again.',
};

export function workspaceFailureMessage(code: string | null) {
  return code && recovery[code]
    ? recovery[code]
    : 'The operation could not finish. Your last saved checkpoint is retained. Check the IDE connection and try again.';
}

/** True when the platform has specific recovery copy for this code. */
export function hasRecoveryMessage(code: string | null) {
  return Boolean(code && recovery[code]);
}
